import type { NurseResponse } from "@vTypes/nurse-type";
import { useMemo, useState } from "react";
import SideMultiSelect from "@components/common/SideMultiSelect";
import LabeledSelect from "@components/common/LabeledSelect";
import CustomModal from "@/components/modal/CustomModal";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { BtnSimpleIcon } from "@/components/ui/button/btn-simple-icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { ChevronDownIcon, Pen, Trash2 } from "lucide-react";
import {
  buildMinDateDisabled,
  formatDateString,
  parseDateString,
} from "./EditSideProfile/reasonPanels/datePickerRules";

interface PrecepteeAssignmentPeriod {
  startDate: string;
  expectedEndDate: string;
}

interface NursePreceptorSectionProps {
  preceptorMode: "preceptor" | "preceptee" | null;
  selectedPreceptees: string[];
  preceptorId: string | null;
  availablePreceptees: { label: string; value: string }[];
  preceptorList: NurseResponse[];
  nurseId?: string;
  precepteeAssignmentPeriod?: PrecepteeAssignmentPeriod | null;
  preceptorAssignmentPeriods?: Record<string, PrecepteeAssignmentPeriod>;
  isPrecepteeStartDateError?: boolean;
  onModeChange: (mode: "preceptor" | "preceptee") => void;
  onPrecepteesChange: (values: string[]) => void;
  onPreceptorChange: (value: string | null) => void;
  onPrecepteeAssignmentPeriodChange?: (
    value: PrecepteeAssignmentPeriod
  ) => void;
  onPreceptorAssignmentPeriodChange?: (
    nurseId: string,
    value: PrecepteeAssignmentPeriod
  ) => void;
  disabled?: boolean;
}

const EMPTY_PERIOD: PrecepteeAssignmentPeriod = {
  startDate: "",
  expectedEndDate: "",
};

export default function NursePreceptorSection({
  preceptorMode,
  selectedPreceptees,
  preceptorId,
  availablePreceptees,
  preceptorList,
  nurseId,
  precepteeAssignmentPeriod,
  preceptorAssignmentPeriods = {},
  isPrecepteeStartDateError,
  onModeChange,
  onPrecepteesChange,
  onPreceptorChange,
  onPrecepteeAssignmentPeriodChange,
  onPreceptorAssignmentPeriodChange,
  disabled,
}: NursePreceptorSectionProps) {
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);
  const [editingPrecepteeId, setEditingPrecepteeId] = useState<string | null>(
    null
  );
  const [preceptorPeriodDraft, setPreceptorPeriodDraft] =
    useState<PrecepteeAssignmentPeriod>(EMPTY_PERIOD);
  const [isPreceptorStartPickerOpen, setIsPreceptorStartPickerOpen] =
    useState(false);
  const [isPreceptorEndPickerOpen, setIsPreceptorEndPickerOpen] =
    useState(false);
  const [isPreceptorPeriodError, setIsPreceptorPeriodError] = useState(false);

  const precepteeStartDate = precepteeAssignmentPeriod?.startDate ?? "";
  const precepteeExpectedEndDate =
    precepteeAssignmentPeriod?.expectedEndDate ?? "";
  const selectedStartDate = useMemo(
    () => parseDateString(precepteeStartDate),
    [precepteeStartDate]
  );
  const selectedExpectedEndDate = useMemo(
    () => parseDateString(precepteeExpectedEndDate),
    [precepteeExpectedEndDate]
  );
  const minExpectedEndDate = useMemo(
    () => parseDateString(precepteeStartDate),
    [precepteeStartDate]
  );
  const preceptorDraftStartDate = useMemo(
    () => parseDateString(preceptorPeriodDraft.startDate),
    [preceptorPeriodDraft.startDate]
  );
  const preceptorDraftEndDate = useMemo(
    () => parseDateString(preceptorPeriodDraft.expectedEndDate),
    [preceptorPeriodDraft.expectedEndDate]
  );
  const defaultPrecepteeStartDate = useMemo(
    () => formatDateString(new Date()),
    []
  );

  const selectedPrecepteeRows = useMemo(
    () =>
      selectedPreceptees.map((selectedNurseId) => ({
        nurseId: selectedNurseId,
        name:
          availablePreceptees.find((option) => option.value === selectedNurseId)
            ?.label ?? selectedNurseId,
        period: preceptorAssignmentPeriods[selectedNurseId] ?? {
          startDate: defaultPrecepteeStartDate,
          expectedEndDate: "",
        },
      })),
    [
      availablePreceptees,
      defaultPrecepteeStartDate,
      preceptorAssignmentPeriods,
      selectedPreceptees,
    ]
  );
  const editingPreceptee = useMemo(
    () =>
      selectedPrecepteeRows.find((row) => row.nurseId === editingPrecepteeId) ??
      null,
    [editingPrecepteeId, selectedPrecepteeRows]
  );

  const closePreceptorPeriodModal = () => {
    setEditingPrecepteeId(null);
    setPreceptorPeriodDraft(EMPTY_PERIOD);
    setIsPreceptorStartPickerOpen(false);
    setIsPreceptorEndPickerOpen(false);
    setIsPreceptorPeriodError(false);
  };

  const openPreceptorPeriodModal = (
    selectedNurseId: string,
    period: PrecepteeAssignmentPeriod
  ) => {
    setEditingPrecepteeId(selectedNurseId);
    setPreceptorPeriodDraft(period);
    setIsPreceptorStartPickerOpen(false);
    setIsPreceptorEndPickerOpen(false);
    setIsPreceptorPeriodError(false);
  };

  const removePreceptee = (selectedNurseId: string) => {
    onPrecepteesChange(
      selectedPreceptees.filter((nurseId) => nurseId !== selectedNurseId)
    );
  };

  const commitPreceptorPeriodDraft = () => {
    if (!editingPrecepteeId) return;

    const start = parseDateString(preceptorPeriodDraft.startDate);
    const end = parseDateString(preceptorPeriodDraft.expectedEndDate);
    if (start && end && end < start) {
      setIsPreceptorPeriodError(true);
      return;
    }

    onPreceptorAssignmentPeriodChange?.(
      editingPrecepteeId,
      preceptorPeriodDraft
    );
    closePreceptorPeriodModal();
  };

  return (
    <>
      <div className="flex justify-between items-center col-span-3">
        <label className="text-[15px] font-semibold text-[#939393] font-pretendard">
          프리셉터/프리셉티 지정
        </label>
      </div>

      <div className="grid grid-cols-[150px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-2">
        <div className="mb-[10px]">
          <Select
            value={preceptorMode || "none"}
            onValueChange={(value) => {
              if (value === "none") {
                if (
                  preceptorMode === "preceptor" ||
                  preceptorMode === "preceptee"
                ) {
                  onModeChange(preceptorMode);
                }
              } else {
                onModeChange(value as "preceptor" | "preceptee");
              }
            }}
            disabled={disabled ?? false}
          >
            <SelectTrigger className="w-full h-[35px] border-[#D6D6DE] rounded-[5px] text-[15px] font-pretendard">
              <SelectValue placeholder="선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-sm">지정 안함</span>
              </SelectItem>
              <SelectItem value="preceptor">
                <span className="text-sm">프리셉터 지정</span>
              </SelectItem>
              <SelectItem value="preceptee">
                <span className="text-sm">프리셉티 지정</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {preceptorMode === null && (
          <div className="relative col-span-2">
            <div className="w-full h-[35px] border border-[#D6D6DE] rounded-[5px] bg-gray-50 flex items-center px-[15px] cursor-not-allowed opacity-50">
              <span className="text-[15px] font-pretendard text-[#333333]">
                -
              </span>
              <ChevronDownIcon className="size-4 opacity-50 pointer-events-none flex-shrink-0 ml-auto" />
            </div>
          </div>
        )}

        {preceptorMode === "preceptor" && (
          <div className="relative col-span-2 min-w-0">
            <SideMultiSelect
              label="프리셉티"
              labelColor="#0E59C6"
              value={selectedPreceptees}
              onChange={onPrecepteesChange}
              options={availablePreceptees}
              placeholder="선택하세요"
              className="min-w-0"
              buttonClassName="h-[36px] min-w-0"
              disabled={disabled ?? false}
            />
          </div>
        )}

        {preceptorMode === "preceptor" && selectedPrecepteeRows.length > 0 && (
          <>
            <span className="hidden" />
            <div className="col-span-3 flex flex-col gap-2">
              <div className="flex justify-between pb-1  font-semibold border-b">
                <span className="text-sm font-pretendard text-muted-foreground">
                  프리셉티 ({selectedPrecepteeRows.length})
                </span>
              </div>

              {selectedPrecepteeRows.map(
                ({ nurseId: rowNurseId, name, period }) => (
                  <div
                    key={rowNurseId}
                    className={cn(
                      "rounded-md border grid px-3 py-2 items-center",
                      disabled ? "grid-cols-[1fr]" : "grid-cols-[1fr_auto]"
                    )}
                  >
                    <div className="flex min-w-0 flex-col font-pretendard">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-foreground font-semibold">
                          {name}
                        </p>
                        <p className="text-sm font-medium text-muted-foreground">
                          {period.startDate || "-"} ~{" "}
                          {period.expectedEndDate || "-"}
                        </p>
                      </div>
                    </div>

                    {!disabled && (
                      <div className="flex items-center gap-1">
                        <BtnSimpleIcon
                          tone="default"
                          onClick={() =>
                            openPreceptorPeriodModal(rowNurseId, period)
                          }
                          aria-label="프리셉티 기간 수정"
                          icon={<Pen className="size-3.5" />}
                        />
                        <BtnSimpleIcon
                          tone="destructive"
                          onClick={() => removePreceptee(rowNurseId)}
                          aria-label="프리셉티 삭제"
                          icon={<Trash2 className="size-3.5" />}
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </>
        )}

        {preceptorMode === "preceptee" && (
          <>
            <div className="relative col-span-2">
              <LabeledSelect
                label="프리셉터"
                labelColor="#0E59C6"
                value={preceptorId || ""}
                onChange={(value) =>
                  onPreceptorChange(value === "" ? null : String(value))
                }
                options={[
                  { label: "선택하세요", value: "" },
                  ...preceptorList
                    .filter((n) => n.nurse_id !== nurseId)
                    .map((n) => ({
                      label: n.name,
                      value: n.nurse_id as string,
                    })),
                ]}
                placeholder="선택하세요"
                disabled={disabled ?? false}
              />
            </div>

            {preceptorId && (
              <>
                <span className="hidden" />
                <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2 col-span-3">
                  <div className="flex flex-col gap-1">
                    <span className="font-pretendard text-[12px] font-semibold text-[#939393]">
                      시작일<span className="text-[#C0392B]">*</span>
                    </span>
                    <Popover
                      open={isStartPickerOpen}
                      onOpenChange={setIsStartPickerOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={disabled ?? false}
                          className={cn(
                            "font-pretendard h-[35px] w-full justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0",
                            isPrecepteeStartDateError
                              ? "border-destructive"
                              : "border-[#D6D6DE]"
                          )}
                        >
                          <span className="truncate">
                            {precepteeStartDate || "-"}
                          </span>
                          <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#939393]" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedStartDate}
                          defaultMonth={selectedStartDate}
                          onSelect={(date) => {
                            if (!date) return;
                            const nextStartDate = formatDateString(date);
                            const nextStart = parseDateString(nextStartDate);
                            const currentExpectedEnd = parseDateString(
                              precepteeExpectedEndDate
                            );
                            const nextExpectedEndDate =
                              nextStart &&
                              currentExpectedEnd &&
                              currentExpectedEnd < nextStart
                                ? ""
                                : precepteeExpectedEndDate;

                            onPrecepteeAssignmentPeriodChange?.({
                              startDate: nextStartDate,
                              expectedEndDate: nextExpectedEndDate,
                            });
                            setIsStartPickerOpen(false);
                          }}
                          captionLayout="label"
                        />
                      </PopoverContent>
                    </Popover>
                    {isPrecepteeStartDateError && (
                      <p className="font-pretendard text-destructive text-xs">
                        * 필수 값입니다
                      </p>
                    )}
                  </div>
                  <span className="pb-[8px] text-[#B2B2B8]">~</span>
                  <div className="flex flex-col gap-1">
                    <span className="font-pretendard text-[12px] font-semibold text-[#939393]">
                      종료예정일<span className="text-[#C0392B]">*</span>
                    </span>
                    <Popover
                      open={isEndPickerOpen}
                      onOpenChange={setIsEndPickerOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={(disabled ?? false) || !precepteeStartDate}
                          className="font-pretendard h-[35px] w-full justify-between border-[#D6D6DE] px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0"
                        >
                          <span className="truncate">
                            {precepteeExpectedEndDate || "-"}
                          </span>
                          <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#939393]" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedExpectedEndDate}
                          defaultMonth={
                            selectedExpectedEndDate ??
                            minExpectedEndDate ??
                            undefined
                          }
                          onSelect={(date) => {
                            onPrecepteeAssignmentPeriodChange?.({
                              startDate: precepteeStartDate,
                              expectedEndDate: date
                                ? formatDateString(date)
                                : "",
                            });
                            setIsEndPickerOpen(false);
                          }}
                          disabled={buildMinDateDisabled(minExpectedEndDate)}
                          captionLayout="label"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <CustomModal.Confirm
        isOpen={editingPrecepteeId != null}
        onClose={closePreceptorPeriodModal}
        onConfirm={commitPreceptorPeriodDraft}
        confirmText="수정"
        cancelText="닫기"
        contentClassName="w-[420px]"
      >
        <div className="flex flex-col gap-4 pb-5">
          <h2 className="pt-1 font-pretendard text-lg text-sig text-center font-semibold">
            프리셉티 기간 변경
          </h2>
          <div className="flex bg-muted/50 p-2 rounded-lg min-w-0 items-center justify-center gap-2 font-pretendard">
            <span className="max-w-[220px] truncate text-[16px] font-semibold text-[#333333]">
              {editingPreceptee?.name ?? "-"}
            </span>
            <span className="shrink-0 rounded-[4px]  px-2 py-1 text-[12px] font-medium text-[#70707A] bg-white rounded-full ">
              {editingPreceptee?.nurseId ?? "-"}
            </span>
          </div>
          <div className="flex flex-col gap-2 rounded-[8px] border border-[#E8E8EE] px-3 py-3">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
              <label className="text-xs! font-semibold text-[#939393]">
                시작일<span className="text-[#C0392B]">*</span>
              </label>
              <span />
              <label className="text-xs! font-semibold text-[#939393]">
                종료예정일<span className="text-[#C0392B]">*</span>
              </label>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex-1 min-w-0">
                <Popover
                  open={isPreceptorStartPickerOpen}
                  onOpenChange={setIsPreceptorStartPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-[35px] items-center justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0 border-[#D6D6DE]"
                    >
                      <span className="truncate text-sm">
                        {preceptorPeriodDraft.startDate || "YYYY-MM-DD"}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 text-[#939393] shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 " align="start">
                    <Calendar
                      mode="single"
                      selected={preceptorDraftStartDate}
                      defaultMonth={preceptorDraftStartDate}
                      onSelect={(date) => {
                        if (!date) return;
                        const nextStartDate = formatDateString(date);
                        const nextStart = parseDateString(nextStartDate);
                        const currentEnd = parseDateString(
                          preceptorPeriodDraft.expectedEndDate
                        );
                        setPreceptorPeriodDraft((prev) => ({
                          startDate: nextStartDate,
                          expectedEndDate:
                            nextStart && currentEnd && currentEnd < nextStart
                              ? ""
                              : prev.expectedEndDate,
                        }));
                        setIsPreceptorPeriodError(false);
                        setIsPreceptorStartPickerOpen(false);
                      }}
                      captionLayout="label"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <span className="text-[#B2B2B8]">~</span>

              <div className="flex-1 min-w-0">
                <Popover
                  open={isPreceptorEndPickerOpen}
                  onOpenChange={setIsPreceptorEndPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!preceptorPeriodDraft.startDate}
                      className="w-full h-[35px] items-center justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0 border-[#D6D6DE]"
                    >
                      <span className="truncate text-sm">
                        {preceptorPeriodDraft.expectedEndDate || "YYYY-MM-DD"}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 text-[#939393] shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={preceptorDraftEndDate}
                      defaultMonth={
                        preceptorDraftEndDate ??
                        preceptorDraftStartDate ??
                        undefined
                      }
                      onSelect={(date) => {
                        setPreceptorPeriodDraft((prev) => ({
                          ...prev,
                          expectedEndDate: date ? formatDateString(date) : "",
                        }));
                        setIsPreceptorPeriodError(false);
                        setIsPreceptorEndPickerOpen(false);
                      }}
                      disabled={buildMinDateDisabled(preceptorDraftStartDate)}
                      captionLayout="label"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          {isPreceptorPeriodError && (
            <p className="text-destructive text-[12px]">
              종료예정일은 시작일 이후로 선택해주세요.
            </p>
          )}
        </div>
      </CustomModal.Confirm>
    </>
  );
}
